// moduleReadOnlyCall.js — the shell's enforcement of an app verb the manifest
// marks `readOnly: true` (MODULE-FN-READONLY-ENFORCED-1; Nika 2026-10-05:
// "Yes, enforced read-only").
//
// An `aiFunctions` entry `{ name, description, readOnly: true }` lets the
// assistant run that verb WITHOUT the Run / Skip row. What makes that safe is
// not the label but this file: the verb runs on a READ-ONLY CALLER, so a
// mislabelled verb cannot write.
//
// How. The transport runs the verb as `caller.call(fn, …args)` where `caller`
// wraps the invoking element in a Proxy. smbls `el.call` resolves the fn on
// `this.context` and invokes it with `this` = the caller, and app code reaches
// the platform the same way (`el.call('moduleData', …)`, `el.call('valData',
// …)` → `el.call('moduleData', …)`), so `this` stays the read-only caller down
// every helper chain. The caller's `context` hands out GUARDED copies of the
// platform's writing surfaces:
//   - moduleData: records list / get / subscribe and the schema reads pass;
//     create / update / upsert / remove and integration calls are refused;
//   - the action verbs (propose / approve / reject), file create / update /
//     upload, calendar writes, notifications, the AI chat and AI stream
//     bridges (both spend credits), AI settings and a mounted library's
//     imperative API (moduleApi / mount / unmount) are refused;
//   - the shell's own AI data ops (aiCreateTicket, aiSendMessage, …), passed
//     in by the transport, are refused.
// Everything else (the app's own helpers, reads, polyglot, opening a page)
// runs as before. The bridges ALSO check `this` themselves (moduleDataBridge,
// moduleActions), so a write reached with the read-only caller as `this` is
// refused even when it did not come through the guarded context.
//
// A refused call never reaches the SDK. It answers in the bridge's own error
// shape (moduleData rejects, the others answer { ok: false, error }), and the
// call record lists it; the transport then marks the verb's result ok:false
// with what was refused, so the assistant never reports a refused change as
// done.
//
// Scope: per CALLER, never a global flag — a write the member makes in the app
// UI while a read-only verb is running is not affected.

export const READ_ONLY_CALL = Symbol.for('symbols.workspace.readOnlyCall')

// The read-only call record riding `el` (a read-only caller), or null.
export const readOnlyCallOf = (el) => {
  try {
    return (el && el[READ_ONLY_CALL]) || null
  } catch (_) {
    return null
  }
}

const _readOnlyBareEntity = (from) => {
  const s = String(from || '').trim()
  const dot = s.lastIndexOf('.')
  return (dot >= 0 ? s.slice(dot + 1) : s).toLowerCase()
}

// moduleData(from, op, args, cb): reads of the record store and of the two
// schema entities only.
const RECORD_READ_OPS = new Set(['list', 'get', 'subscribe'])
const META_READ_OPS = new Set(['list', 'get'])
export const isReadOnlyDataCall = (from, op) => {
  const entity = _readOnlyBareEntity(from)
  const operation = String(op || '').toLowerCase()
  if (entity === 'records') return RECORD_READ_OPS.has(operation)
  if (entity === 'recordcollections' || entity === 'fielddefs') return META_READ_OPS.has(operation)
  return false
}

// Surfaces that pass only for some arguments.
const CONDITIONAL = {
  moduleData: (from, op) => isReadOnlyDataCall(from, op),
  moduleAiAdmin: (op) => op === 'usage' || op === 'credits'
}

// Surfaces that write, send, spend, or drive a mounted library.
const REFUSED = [
  'proposeModuleAction',
  'approveAiAction',
  'rejectAiAction',
  'moduleFileUpload',
  'moduleFileUploadLarge',
  'moduleFileUpdate',
  'moduleFileCreate',
  'moduleCalendarCreate',
  'moduleCalendarUpdate',
  'moduleCalendarRemove',
  'moduleNotify',
  'moduleNotifyClose',
  'moduleAiChat',
  'moduleAiStream',
  'moduleAsk',
  'moduleApi',
  'moduleMount',
  'moduleUnmount'
]

// How each refused surface answers, in its own convention.
const REJECTS = new Set(['moduleData'])
const NULLS = new Set(['moduleApi', 'moduleMount', 'moduleUnmount'])

const _describe = (name, args) => {
  if (name === 'moduleData') {
    const entity = String(args[0] || '')
    const op = String(args[1] || '')
    const coll = args[2] && typeof args[2] === 'object' && args[2].collection
    return `moduleData ${op} on ${entity}${coll ? ` (${coll})` : ''}`
  }
  if (name === 'moduleAiAdmin') return `moduleAiAdmin ${String(args[0] || '')}`
  return name
}

// Refuse one call: record it, warn once per call record, answer in the
// surface's own shape.
export function refuseInReadOnlyCall(record, name, args = []) {
  const what = _describe(name, args)
  const message = `[module] read-only call: "${record.fn || 'this function'}" is declared readOnly — ${what} is refused (it may only read records).`
  if (record) {
    record.refused.push(what)
    if (record.refused.length === 1) {
      try {
        console.warn(message)
      } catch (_) {}
    }
  }
  if (REJECTS.has(name)) return Promise.reject(new Error(message))
  if (NULLS.has(name)) return null
  return { ok: false, error: message, code: 'read_only_call' }
}

// Is this call allowed inside a read-only call? `refuse` — extra refused names
// (the transport's own AI data ops).
export const readOnlyVerdict = (name, args, refuse) => {
  if (Object.prototype.hasOwnProperty.call(CONDITIONAL, name)) return !!CONDITIONAL[name](...args)
  return !refuse.has(name)
}

const BAGS = new Set(['utils', 'functions', 'methods', 'snippets'])

/**
 * A read-only caller over `el` for one verb run. `opts.fn` names the verb (for
 * the message); `opts.refuse` — extra names to refuse (the transport's AI data
 * ops). The record (`readOnlyCallOf(caller)`) lists what was refused.
 */
export function readOnlyCaller(el, opts = {}) {
  const record = { fn: String(opts.fn || ''), refused: [] }
  const refuse = new Set([...REFUSED, ...(Array.isArray(opts.refuse) ? opts.refuse : [])])
  const guarded = new Map()
  let caller = null
  let contextProxy = null
  const bagProxies = new Map()

  const guardOf = (bagName, bagProxy, name, real) => {
    const key = bagName + ':' + name
    if (guarded.has(key) && guarded.get(key).real === real) return guarded.get(key).fn
    const fn = function readOnlyGuard(...args) {
      if (!readOnlyVerdict(name, args, refuse)) return refuseInReadOnlyCall(record, name, args)
      // Called off the bag itself (`ctx.functions.moduleData(…)`) or bare:
      // the real surface still sees the read-only caller as `this`.
      const self = this === bagProxy || this === undefined || this === null ? caller : this
      return real.apply(self, args)
    }
    guarded.set(key, { real, fn })
    return fn
  }

  const bagProxyOf = (bagName, bag) => {
    if (!bag || typeof bag !== 'object') return bag
    const hit = bagProxies.get(bagName)
    if (hit && hit.bag === bag) return hit.proxy
    const proxy = new Proxy(bag, {
      get(target, prop, receiver) {
        const real = Reflect.get(target, prop, receiver)
        if (typeof prop !== 'string' || typeof real !== 'function') return real
        const watched = refuse.has(prop) || Object.prototype.hasOwnProperty.call(CONDITIONAL, prop)
        return watched ? guardOf(bagName, proxy, prop, real) : real
      }
    })
    bagProxies.set(bagName, { bag, proxy })
    return proxy
  }

  const contextOf = (ctx) => {
    if (!ctx || typeof ctx !== 'object') return ctx
    if (contextProxy && contextProxy.ctx === ctx) return contextProxy.proxy
    const proxy = new Proxy(ctx, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver)
        return typeof prop === 'string' && BAGS.has(prop) ? bagProxyOf(prop, value) : value
      }
    })
    contextProxy = { ctx, proxy }
    return proxy
  }

  caller = new Proxy(el, {
    get(target, prop) {
      if (prop === READ_ONLY_CALL) return record
      // `this` inside element methods (call, update, …) is the caller, so a
      // nested el.call stays read-only; plain reads come off the element.
      const value = Reflect.get(target, prop, caller)
      return prop === 'context' ? contextOf(value) : value
    },
    set(target, prop, value) {
      return Reflect.set(target, prop, value, target)
    }
  })
  return caller
}

/**
 * Fold a read-only call's refusals into the verb's result: a verb that tried
 * to change something answers ok:false with what was refused (the read fields
 * it returned stay), so the assistant never reports a refused change as done.
 */
export function withReadOnlyOutcome(res, record) {
  if (!record || !record.refused.length) return res
  const error = `This read-only function tried to change data (${record.refused.join('; ')}); the change was refused and nothing was written.`
  const base = res && typeof res === 'object' && !Array.isArray(res) ? res : { result: res }
  return { ...base, ok: false, error, readOnlyRefused: record.refused.slice() }
}
